import React, { useEffect, useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  getFleetKpi, getSpesePerTipo, getSpeseMessili, getSpesePerMezzo,
  getScadenzeUrgenti,
  FleetKpi, Scadenza,
} from '../../services/fleet';

const MESI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

const COLORS_PIE = [
  '#f97316','#3b82f6','#22c55e','#a855f7',
  '#ef4444','#eab308','#06b6d4','#ec4899','#84cc16','#f59e0b',
];

const TIPO_ICONS: Record<string, string> = {
  carburante: '⛽', manutenzione: '🔧', bollo: '📋',
  assicurazione: '🛡️', pedaggi: '🛣️', tagliando: '🔩',
  revisione: '🔍', cambio_gomme: '🛞', multa: '🚨', altro: '📌',
};

interface SpesaTipo   { tipo_spesa: string; totale: number; n_voci: number; }
interface SpesaMensile { mese: number; tipo_spesa: string; totale: number; }
interface SpesaMezzo  { mezzo_id: number; targa: string; modello: string; totale: number; }

export default function DashboardFleet() {
  const [kpi, setKpi]                     = useState<FleetKpi | null>(null);
  const [spesePerTipo, setSpesePerTipo]   = useState<SpesaTipo[]>([]);
  const [speseMensili, setSpeseMensili]   = useState<SpesaMensile[]>([]);
  const [spesePerMezzo, setSpesePerMezzo] = useState<SpesaMezzo[]>([]);
  const [scadenzeUrgenti, setScadenzeUrgenti] = useState<Scadenza[]>([]);
  const [anno, setAnno]   = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const anni = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => { loadAll(); }, [anno]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiRes, tipoRes, mensileRes, mezzoRes, scadRes] = await Promise.all([
        getFleetKpi({ data_da: `${anno}-01-01`, data_a: `${anno}-12-31` }),
        getSpesePerTipo({ data_da: `${anno}-01-01`, data_a: `${anno}-12-31` }),
        getSpeseMessili(anno),
        getSpesePerMezzo({ data_da: `${anno}-01-01`, data_a: `${anno}-12-31` }),
        getScadenzeUrgenti(60),
      ]);
      setKpi(kpiRes.data);
      setSpesePerTipo(tipoRes.data);
      setSpeseMensili(mensileRes.data);
      setSpesePerMezzo(mezzoRes.data);
      setScadenzeUrgenti(scadRes.data);
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  };

  const barData = MESI.map((label, i) => {
    const mese = i + 1;
    const voci = speseMensili.filter(s => s.mese === mese);
    const obj: Record<string, number | string> = { mese: label };
    voci.forEach(v => { obj[v.tipo_spesa] = v.totale; });
    return obj;
  });

  const tipiBar = [...new Set(speseMensili.map(s => s.tipo_spesa))];

  if (loading) return <div className="fleet-loading" style={{ padding: 60 }}>Caricamento dashboard...</div>;

  return (
    <div className="fleet-dashboard">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <select className="fleet-filter" value={anno} onChange={e => setAnno(parseInt(e.target.value))}>
          {anni.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="dash-kpi-grid">
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon">🚗</div>
            <div className="dash-kpi-label">Veicoli attivi</div>
            <div className="dash-kpi-value">{kpi.n_mezzi_attivi}</div>
          </div>
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon">💰</div>
            <div className="dash-kpi-label">Totale spese {anno}</div>
            <div className="dash-kpi-value">
              € {kpi.totale_spese_periodo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="dash-kpi-card">
            <div className="dash-kpi-icon">⛽</div>
            <div className="dash-kpi-label">Carburante {anno}</div>
            <div className="dash-kpi-value">
              € {kpi.totale_carburante_periodo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className={`dash-kpi-card ${kpi.n_scadenze_scadute > 0 ? 'kpi-danger' : kpi.n_scadenze_urgenti > 0 ? 'kpi-warning' : ''}`}>
            <div className="dash-kpi-icon">📅</div>
            <div className="dash-kpi-label">Scadenze urgenti (60gg)</div>
            <div className="dash-kpi-value">{kpi.n_scadenze_urgenti}</div>
            {kpi.n_scadenze_scadute > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>
                {kpi.n_scadenze_scadute} già scadute
              </div>
            )}
          </div>
          <div className={`dash-kpi-card ${kpi.totale_multe_aperte > 0 ? 'kpi-warning' : ''}`}>
            <div className="dash-kpi-icon">🚨</div>
            <div className="dash-kpi-label">Multe non pagate</div>
            <div className="dash-kpi-value">
              € {kpi.totale_multe_aperte.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      )}

      {/* Riga 1: Torta + Barre per veicolo */}
      <div className="dash-charts-row">
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">⛽ Spese per categoria</h3>
          {spesePerTipo.length === 0 ? (
            <div className="dash-empty">Nessun dato</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={spesePerTipo}
                    dataKey="totale"
                    nameKey="tipo_spesa"
                    cx="50%" cy="50%"
                    outerRadius={95}
                    label={({ tipo_spesa, percent }) =>
                      `${TIPO_ICONS[tipo_spesa] || ''} ${(percent * 100).toFixed(1)}%`
                    }
                  >
                    {spesePerTipo.map((_, i) => (
                      <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val: number, name: string) => [
                      `€ ${Number(val).toFixed(2)}`,
                      `${TIPO_ICONS[name] || ''} ${name}`,
                    ]}
                  />
                  <Legend formatter={(val: string) => `${TIPO_ICONS[val] || ''} ${val.replace(/_/g, ' ')}`} />
                </PieChart>
              </ResponsiveContainer>
              <table style={{ width: '100%', fontSize: '0.8rem', marginTop: 8 }}>
                <thead>
                  <tr style={{ color: '#6b7280' }}>
                    <th style={{ textAlign: 'left' }}>Categoria</th>
                    <th style={{ textAlign: 'right' }}>N.</th>
                    <th style={{ textAlign: 'right' }}>Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {spesePerTipo.map((s, i) => (
                    <tr key={s.tipo_spesa} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 0' }}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10,
                          background: COLORS_PIE[i % COLORS_PIE.length],
                          borderRadius: 2, marginRight: 6,
                        }} />
                        {TIPO_ICONS[s.tipo_spesa]} {s.tipo_spesa.replace(/_/g, ' ')}
                      </td>
                      <td style={{ textAlign: 'right', color: '#6b7280' }}>{s.n_voci}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>€ {s.totale.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="dash-chart-card">
          <h3 className="dash-chart-title">🚗 Top spese per veicolo</h3>
          {spesePerMezzo.length === 0 ? (
            <div className="dash-empty">Nessun dato</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={spesePerMezzo.slice(0, 10)}
                layout="vertical"
                margin={{ left: 20, right: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={v => `€${v}`} fontSize={11} />
                <YAxis type="category" dataKey="targa" width={70} fontSize={11} />
                <Tooltip formatter={(v: number) => [`€ ${Number(v).toFixed(2)}`, 'Totale']} />
                <Bar dataKey="totale" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Riga 2: Trend mensile stacked */}
      <div className="dash-chart-card" style={{ marginBottom: 24 }}>
        <h3 className="dash-chart-title">📈 Trend spese mensile {anno}</h3>
        {barData.every(d => Object.keys(d).length === 1) ? (
          <div className="dash-empty">Nessun dato per {anno}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mese" fontSize={12} />
              <YAxis tickFormatter={v => `€${v}`} fontSize={11} />
              <Tooltip formatter={(v: number, name: string) => [`€ ${Number(v).toFixed(2)}`, name.replace(/_/g, ' ')]} />
              <Legend formatter={(val: string) => `${TIPO_ICONS[val] || ''} ${val.replace(/_/g, ' ')}`} />
              {tipiBar.map((tipo, i) => (
                <Bar
                  key={tipo}
                  dataKey={tipo}
                  stackId="a"
                  fill={COLORS_PIE[i % COLORS_PIE.length]}
                  name={tipo}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Scadenze urgenti */}
      {scadenzeUrgenti.length > 0 && (
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">⚠️ Scadenze prossime (60 giorni)</h3>
          <table className="fleet-table">
            <thead>
              <tr>
                <th>Veicolo</th>
                <th>Tipo</th>
                <th>Scadenza</th>
                <th>Giorni</th>
                <th>Stato</th>
                <th>Costo</th>
              </tr>
            </thead>
            <tbody>
              {scadenzeUrgenti.map(s => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.mezzo_targa}</strong>{' '}
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{s.mezzo_modello}</span>
                  </td>
                  <td>{s.tipologia}</td>
                  <td>{new Date(s.data_scadenza).toLocaleDateString('it-IT')}</td>
                  <td style={{
                    color: (s.giorni_alla_scadenza || 0) < 0 ? '#ef4444'
                         : (s.giorni_alla_scadenza || 0) <= 15 ? '#f59e0b'
                         : '#374151',
                    fontWeight: 600,
                  }}>
                    {s.giorni_alla_scadenza !== undefined
                      ? s.giorni_alla_scadenza < 0
                        ? `Scaduta ${Math.abs(s.giorni_alla_scadenza)}gg fa`
                        : `${s.giorni_alla_scadenza} gg`
                      : '—'}
                  </td>
                  <td>
                    <span style={{
                      background: s.stato === 'scaduta' ? '#fee2e2' : s.stato === 'in_scadenza' ? '#fef9c3' : '#dcfce7',
                      color:      s.stato === 'scaduta' ? '#991b1b' : s.stato === 'in_scadenza' ? '#854d0e' : '#15803d',
                      padding: '2px 8px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                    }}>
                      {s.stato === 'scaduta' ? '❌ Scaduta' : s.stato === 'in_scadenza' ? '⚠️ In scadenza' : '✅ OK'}
                    </span>
                  </td>
                  <td>{s.costo ? `€ ${Number(s.costo).toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .fleet-dashboard { padding: 4px 0; }

        .dash-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 14px;
          margin-bottom: 24px;
        }

        .dash-kpi-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 16px;
          text-align: center;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }

        .dash-kpi-card.kpi-danger  { border-color: #fecaca; background: #fff5f5; }
        .dash-kpi-card.kpi-warning { border-color: #fde68a; background: #fffef0; }

        .dash-kpi-icon  { font-size: 1.6rem; margin-bottom: 6px; }
        .dash-kpi-label { font-size: 0.72rem; color: #6b7280; font-weight: 600; text-transform: uppercase; margin-bottom: 4px; }
        .dash-kpi-value { font-size: 1.3rem; font-weight: 700; color: #111827; }

        .dash-charts-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }

        @media (max-width: 900px) {
          .dash-charts-row { grid-template-columns: 1fr; }
        }

        .dash-chart-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }

        .dash-chart-title {
          font-size: 0.95rem;
          font-weight: 700;
          color: #374151;
          margin: 0 0 16px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f3f4f6;
        }

        .dash-empty {
          text-align: center;
          color: #9ca3af;
          padding: 40px;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
